import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FaPaperclip, FaEnvelope } from 'react-icons/fa';
import axios from 'axios';

const EmailModal = ({ show, handleClose, clientName, clientEmail, clientId }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleAttachmentChange = (e) => {
    if (e.target.files) {
      const newAttachments = [...attachments];
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      const maxFiles = 5;
      
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        
        // Check file size
        if (file.size > maxFileSize) {
          setError(`File "${file.name}" is too large. Maximum file size is 10MB.`);
          return;
        }
        
        // Check total number of files
        if (newAttachments.length >= maxFiles) {
          setError(`Maximum ${maxFiles} attachments allowed.`);
          return;
        }
        
        newAttachments.push(file);
      }
      
      setAttachments(newAttachments);
      setError(null); // Clear any previous errors
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    
    setIsSending(true);
    setError(null);
    
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      let response;
      
      if (attachments.length > 0) {
        // Send email with attachments using FormData
        const formData = new FormData();
        formData.append('to', clientEmail);
        formData.append('subject', subject);
        formData.append('message', message);
        if (clientId) {
          formData.append('clientId', clientId);
        }
        
        // Add all attachments to FormData
        attachments.forEach((file) => {
          formData.append('attachments', file);
        });
        
        response = await axios.post(
          '/api/email/send-client-email-with-attachments',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'x-auth-token': token
            }
          }
        );
      } else {
        // Send email without attachments using JSON
        response = await axios.post(
          '/api/email/send-client-email',
          {
            to: clientEmail,
            subject: subject,
            message: message,
            clientId: clientId
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          }
        );
      }
      
      console.log('Email API response:', response.data);
      
      if (response.data.success) {
        // Success
        setSuccess(true);
        setTimeout(() => {
          handleReset();
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.data.msg || 'Failed to send email');
      }
    } catch (err) {
      console.error('Email error:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleReset = () => {
    setSubject('');
    setMessage('');
    setAttachments([]);
    setError(null);
    setSuccess(false);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelope className="me-2" />
          Email to {clientName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success">
            Email sent successfully{attachments.length > 0 ? ` with ${attachments.length} attachment(s)` : ''}!
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>To:</Form.Label>
            <Form.Control type="email" value={clientEmail} readOnly />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Subject:</Form.Label>
            <Form.Control 
              type="text" 
              value={subject} 
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Message:</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={6} 
              value={message} 
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message here"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Attachments:</Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Maximum 5 files, 10MB per file
            </Form.Text>
            
            {attachments.length > 0 && (
              <div className="mb-3">
                <ul className="list-group">
                  {attachments.map((file, index) => (
                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeAttachment(index)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="d-grid">
              <Button variant="outline-secondary" as="label" htmlFor="file-upload">
                <FaPaperclip className="me-2" />
                Add Attachment
                <Form.Control
                  id="file-upload"
                  type="file"
                  onChange={handleAttachmentChange}
                  multiple
                  style={{ display: 'none' }}
                />
              </Button>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={isSending || success}
        >
          {isSending ? 'Sending...' : `Send Email${attachments.length > 0 ? ` with ${attachments.length} attachment(s)` : ''}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailModal;